import GuideLayout from "../components/GuideLayout";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Cruise port transfer and shore excursion",
  name: "Livorno & La Spezia Cruise Port Transfers",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Livorno", "La Spezia", "Florence", "Pisa", "Lucca", "Cinque Terre", "Tuscany"],
  description:
    "Private Mercedes V-Class cruise-port transfers and bespoke shore excursions from the ports of Livorno and La Spezia to Florence, Pisa, Lucca and the Cinque Terre.",
  url: "https://yourblacklimo.com/livorno-cruise-transfers",
};

const CONTENT = {
  en: {
    seoTitle: "Livorno & La Spezia Cruise Port Transfers | Private Shore Excursions — Your Black Limo",
    seoDesc:
      "Private Mercedes V-Class cruise-port transfers and bespoke shore excursions from Livorno and La Spezia to Florence, Pisa, Lucca and the Cinque Terre. English-speaking chauffeur, ship tracking, no queues.",
    overline: "Cruise Port Transfers",
    h1: "Shore Excursions & Private Transfers from Livorno and La Spezia",
    intro:
      "Step off the ship and into a spotless Mercedes V-Class. We turn a few precious hours in port into an unhurried private day across Tuscany and the Ligurian coast — and we always bring you back to the pier with time to spare.",
    heroAlt: "Private cruise shore excursion in Tuscany with a Mercedes V-Class",
    blocks: [
      {
        title: "Port of Livorno",
        body: "The gateway to Tuscany. From the Livorno cruise terminal we reach Florence in about 90 minutes, Pisa in 30 and Lucca in 45 — with time to spare before your all-aboard call. Your chauffeur waits pier-side with a name board and tracks your ship's arrival in real time.",
      },
      {
        title: "Port of La Spezia",
        body: "The doorway to the Cinque Terre and the Ligurian Riviera. From La Spezia we design relaxed days along the five villages, Portovenere and Lerici, or head inland to Lucca and Pisa. No queues, no crowds — only a private car and an itinerary built around you.",
      },
    ],
    cards: {
      overline: "Popular Shore Excursions",
      title: "Where your day in port could take you",
      items: [
        { title: "Florence Highlights", body: "The Duomo, Ponte Vecchio, Michelangelo's David and the Renaissance old town — with a private driver on hand all day." },
        { title: "Pisa & Lucca", body: "The Leaning Tower and Piazza dei Miracoli, then the cycle-quiet Renaissance walls of Lucca — two icons in one gentle day." },
        { title: "Cinque Terre", body: "From La Spezia, a coastal escape to Monterosso, Vernazza and the pastel villages clinging to the sea." },
        { title: "Chianti & Wine", body: "Vineyards, hilltop villages and the slow, golden side of Tuscany." },
      ],
    },
    note: {
      title: "How it works",
      body:
        "We track your ship's arrival and meet you pier-side with a name board. Every excursion is private — no shared coaches, no fixed groups. Your chauffeur adapts the pace to you, suggests the tables and viewpoints worth stopping for, and guarantees a comfortable margin for your all-aboard time. Fully licensed NCC, professional English-speaking drivers, and a Mercedes V-Class for up to seven guests with luggage.",
    },
    cta: {
      title: "Plan your shore excursion from Livorno or La Spezia",
      secondaryLabel: "View standard tours",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Will I get back to my ship on time?", a: "Always. We track your ship's schedule, build in a generous margin and plan the day around your all-aboard time, so you return to the pier relaxed and never rushed." },
        { q: "How long is the drive from Livorno to Florence?", a: "About 90 minutes each way. Pisa is around 30 minutes and Lucca about 45, leaving plenty of time to explore before returning to port." },
        { q: "Is the shore excursion private?", a: "Yes. Every excursion is fully private in your own Mercedes V-Class with a dedicated chauffeur — never a shared coach or a fixed group." },
        { q: "How many guests can you carry?", a: "Up to seven passengers with luggage in a spotless Mercedes V-Class, with child seats available on request." },
      ],
    },
  },
  it: {
    seoTitle: "Transfer Crociere Livorno e La Spezia | Escursioni Private a Terra — Your Black Limo",
    seoDesc:
      "Transfer privati in Mercedes Classe V e escursioni a terra su misura dai porti di Livorno e La Spezia verso Firenze, Pisa, Lucca e le Cinque Terre. Autista che parla inglese, monitoraggio nave, nessuna coda.",
    overline: "Transfer Crociere",
    h1: "Escursioni a Terra e Transfer Privati da Livorno e La Spezia",
    intro:
      "Scendi dalla nave e sali su un'impeccabile Mercedes Classe V. Trasformiamo poche preziose ore in porto in una giornata privata e senza fretta tra la Toscana e la costa ligure — e ti riportiamo sempre al molo con ampio anticipo.",
    heroAlt: "Escursione privata a terra in Toscana con una Mercedes Classe V",
    blocks: [
      {
        title: "Porto di Livorno",
        body: "La porta della Toscana. Dal terminal crociere di Livorno raggiungiamo Firenze in circa 90 minuti, Pisa in 30 e Lucca in 45 — con tutto il tempo per rientrare prima dell'orario di imbarco. Il tuo autista ti attende al molo con un cartello e monitora l'arrivo della nave in tempo reale.",
      },
      {
        title: "Porto di La Spezia",
        body: "L'ingresso alle Cinque Terre e alla Riviera ligure. Da La Spezia disegniamo giornate rilassate tra i cinque borghi, Portovenere e Lerici, oppure verso l'interno fino a Lucca e Pisa. Nessuna coda, nessuna folla — solo un'auto privata e un itinerario costruito su di te.",
      },
    ],
    cards: {
      overline: "Escursioni Più Richieste",
      title: "Dove può portarti la tua giornata in porto",
      items: [
        { title: "Firenze", body: "Il Duomo, Ponte Vecchio, il David di Michelangelo e il centro rinascimentale — con l'autista a disposizione tutto il giorno." },
        { title: "Pisa e Lucca", body: "La Torre Pendente e Piazza dei Miracoli, poi le mura rinascimentali di Lucca — due icone in una giornata tranquilla." },
        { title: "Cinque Terre", body: "Da La Spezia, una fuga sulla costa a Monterosso, Vernazza e i borghi pastello a picco sul mare." },
        { title: "Chianti e Vino", body: "Vigneti, borghi collinari e il lato lento e dorato della Toscana." },
      ],
    },
    note: {
      title: "Come funziona",
      body:
        "Monitoriamo l'arrivo della nave e ti accogliamo al molo con un cartello. Ogni escursione è privata — niente pullman condivisi, niente gruppi fissi. Il tuo autista adatta il ritmo a te, suggerisce le tavole e i punti panoramici per cui vale la pena fermarsi e garantisce un comodo margine sull'orario di imbarco. NCC regolarmente autorizzato, autisti professionisti che parlano inglese e una Mercedes Classe V fino a sette ospiti con bagagli.",
    },
    cta: {
      title: "Organizza la tua escursione da Livorno o La Spezia",
      secondaryLabel: "Vedi i tour",
      secondaryHref: "/cruise-tours",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Rientrerò alla nave in orario?", a: "Sempre. Monitoriamo gli orari della nave, prevediamo un ampio margine e costruiamo la giornata attorno all'orario di imbarco, così torni al molo rilassato e senza fretta." },
        { q: "Quanto dura il tragitto da Livorno a Firenze?", a: "Circa 90 minuti per tratta. Pisa è a circa 30 minuti e Lucca a circa 45, con tutto il tempo per visitare prima del rientro in porto." },
        { q: "L'escursione a terra è privata?", a: "Sì. Ogni escursione è completamente privata, sulla tua Mercedes Classe V con autista dedicato — mai un pullman condiviso o un gruppo fisso." },
        { q: "Quante persone potete trasportare?", a: "Fino a sette passeggeri con bagagli in un'impeccabile Mercedes Classe V, con seggiolini per bambini su richiesta." },
      ],
    },
  },
};

export default function CruisePortGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="cruise-port-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/livorno-cruise-transfers", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={"https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/d9e1ccc958811042c8cad2ae9effa25a2f2f1db607f044511d65cef07c1ee62e.jpeg"}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
